// Motor de alertas e insights financieros.
//
// Reglas basadas 100% en los datos existentes (events, movements, products).
// Cada finding lleva su `evidence` (los números exactos usados) y su
// `recommendation` calculada a partir de esos mismos números — nada de
// consejos genéricos desconectados de los datos.
//
// Funciones puras (sin llamadas a Supabase) para que sean fáciles de leer,
// probar y auditar.

import { eventCostTotal, eventProfit, lineTotal } from "./calc.js";
import { lastMonths, monthlyMovementTotals, eventsInLastMonths, sum, daysSince } from "./aggregate.js";
import { formatCurrency, formatDate, monthLabel } from "./format.js";

// Los conceptos de costo ya no son fijos: cada paquete define los suyos. La
// regla agrupa por el concepto que realmente se usó, así que si mañana aparece
// "Buffet" o "Castillo inflable", la alerta lo nombra sin tocar código.
function costsByConcept(events) {
  const totals = new Map();
  for (const e of events) {
    for (const line of Array.isArray(e.cost_lines) ? e.cost_lines : []) {
      const key = (line.concept || "sin concepto").trim();
      totals.set(key, (totals.get(key) ?? 0) + lineTotal(line, e));
    }
  }
  return [...totals].map(([label, total]) => ({ label, total })).sort((a, b) => b.total - a.total);
}

function productName(products, id) {
  return products.find((p) => p.id === id)?.name || "Producto sin nombre";
}

// -----------------------------------------------------------------------
// Regla 1: margen estructuralmente negativo por producto
// -----------------------------------------------------------------------
function ruleNegativeMarginProducts(events, products) {
  const findings = [];
  const byProduct = groupBy(events, (e) => e.product_id);

  for (const [productId, productEvents] of byProduct) {
    if (!productId || productEvents.length < 3) continue; // "no incidental" -> mínimo 3 eventos

    const profits = productEvents.map((e) => eventProfit(e));
    const negativeCount = profits.filter((p) => p < 0).length;
    const avgProfit = sum(productEvents, eventProfit) / productEvents.length;
    const avgIncome = sum(productEvents, (e) => e.income) / productEvents.length;
    const avgChildren = sum(productEvents, (e) => e.children_count) / productEvents.length;

    // Estructural: al menos 2 de cada 3 eventos con pérdida, y promedio negativo.
    if (avgProfit >= 0 || negativeCount / productEvents.length < 0.5) continue;

    const name = productName(products, productId);
    const deficitPerEvent = Math.abs(avgProfit);
    const perChildIncrease = avgChildren > 0 ? deficitPerEvent / avgChildren : null;

    findings.push({
      id: `neg-margin-${productId}`,
      severity: avgProfit < -avgIncome * 0.25 ? "alta" : "media",
      title: `${name} da pérdida de forma sostenida`,
      message: `${name} viene con pérdida en ${negativeCount} de los últimos ${productEvents.length} eventos, un promedio de ${formatCurrency(deficitPerEvent)} por evento.`,
      recommendation: perChildIncrease
        ? `Para llegar a punto de equilibrio con la estructura de costos actual, subí el ingreso por evento en al menos ${formatCurrency(deficitPerEvent)} (equivale a ${formatCurrency(perChildIncrease)} más por chico, en promedio).`
        : `Para llegar a punto de equilibrio con la estructura de costos actual, subí el ingreso por evento en al menos ${formatCurrency(deficitPerEvent)}.`,
      evidence: productEvents
        .slice()
        .sort((a, b) => (a.date < b.date ? 1 : -1))
        .map((e) => ({
          date: e.date,
          description: e.description,
          income: e.income,
          cost: eventCostTotal(e),
          profit: eventProfit(e),
        })),
    });
  }

  return findings;
}

// -----------------------------------------------------------------------
// Regla 2: meses donde egresos > ingresos
// -----------------------------------------------------------------------
function ruleDeficitMonths(movements) {
  const months = monthlyMovementTotals(movements, 6);
  const deficitMonths = months.filter((m) => m.egreso > m.ingreso && (m.ingreso > 0 || m.egreso > 0));
  if (deficitMonths.length === 0) return [];

  // ¿son los meses más recientes y consecutivos? eso sube la severidad.
  const trailingDeficit = [];
  for (let i = months.length - 1; i >= 0; i--) {
    if (months[i].egreso > months[i].ingreso) trailingDeficit.push(months[i]);
    else break;
  }

  const label = deficitMonths.map((m) => monthLabel(m.year, m.month)).join(", ");

  return [
    {
      id: "deficit-months",
      severity: trailingDeficit.length >= 2 ? "alta" : "media",
      title:
        trailingDeficit.length >= 2
          ? `${trailingDeficit.length} meses seguidos con más gastos que ingresos`
          : "Hubo meses con más gastos que ingresos",
      message: `En los últimos 6 meses, los egresos superaron a los ingresos en: ${label}.${
        trailingDeficit.length >= 2 ? ` Los últimos ${trailingDeficit.length} meses seguidos vienen así.` : ""
      }`,
      recommendation:
        trailingDeficit.length >= 2
          ? `Revisá qué eventos o gastos fijos se repiten en ${monthLabel(trailingDeficit[trailingDeficit.length - 1].year, trailingDeficit[trailingDeficit.length - 1].month)}–${monthLabel(trailingDeficit[0].year, trailingDeficit[0].month)}: la caja se viene achicando mes a mes.`
          : `Fue puntual, pero conviene revisar qué generó el déficit en ${label} antes de que se repita.`,
      evidence: months.map((m) => ({
        month: monthLabel(m.year, m.month),
        ingreso: m.ingreso,
        egreso: m.egreso,
        balance: m.ingreso - m.egreso,
      })),
    },
  ];
}

// -----------------------------------------------------------------------
// Regla 3: tendencia a la baja en ingresos o cantidad de eventos
// -----------------------------------------------------------------------
function ruleDecliningTrend(events) {
  const recent = eventsInLastMonths(events, 3);
  const previous = eventsInLastMonths(events, 6).filter((e) => !recent.includes(e));

  if (previous.length === 0) return [];

  const recentIncome = sum(recent, (e) => e.income);
  const previousIncome = sum(previous, (e) => e.income);
  const incomeChange = previousIncome > 0 ? (recentIncome - previousIncome) / previousIncome : 0;
  const countChange = previous.length > 0 ? (recent.length - previous.length) / previous.length : 0;

  const findings = [];

  if (incomeChange <= -0.15) {
    findings.push({
      id: "declining-income",
      severity: incomeChange <= -0.3 ? "alta" : "media",
      title: "Los ingresos vienen bajando",
      message: `Los últimos 3 meses facturaron ${formatCurrency(recentIncome)}, contra ${formatCurrency(previousIncome)} de los 3 meses anteriores (${Math.round(incomeChange * 100)}%).`,
      recommendation: "Revisá en la pestaña Productos cuáles vienen empujando la baja para decidir si hace falta reforzar difusión o ajustar precios.",
      evidence: [
        { período: "3 meses anteriores", eventos: previous.length, ingreso: previousIncome },
        { período: "últimos 3 meses", eventos: recent.length, ingreso: recentIncome },
      ],
    });
  }

  if (countChange <= -0.25 && previous.length >= 3) {
    findings.push({
      id: "declining-event-count",
      severity: countChange <= -0.5 ? "alta" : "media",
      title: "Cada vez se organizan menos eventos",
      message: `Se hicieron ${recent.length} eventos en los últimos 3 meses, contra ${previous.length} en los 3 meses anteriores.`,
      recommendation: "Si no fue algo puntual (vacaciones, temporada baja), vale la pena entender por qué bajó la demanda antes de que impacte más el balance.",
      evidence: [
        { período: "3 meses anteriores", eventos: previous.length },
        { período: "últimos 3 meses", eventos: recent.length },
      ],
    });
  }

  return findings;
}

// -----------------------------------------------------------------------
// Regla 4: costos desproporcionados dentro de un producto
// -----------------------------------------------------------------------
function ruleDominantCost(events, products) {
  const findings = [];
  const byProduct = groupBy(events, (e) => e.product_id);

  for (const [productId, productEvents] of byProduct) {
    if (!productId || productEvents.length < 3) continue;

    const totalIncome = sum(productEvents, (e) => e.income);
    const totalCost = sum(productEvents, (e) => eventCostTotal(e));
    if (totalCost <= 0) continue;

    const componentTotals = costsByConcept(productEvents);
    if (componentTotals.length === 0) continue;

    const dominant = componentTotals[0];
    const share = dominant.total / totalCost;
    const marginRatio = totalIncome > 0 ? (totalIncome - totalCost) / totalIncome : -1;

    // sólo alertar si ese costo pesa mucho Y el margen del producto ya es ajustado o negativo
    if (share < 0.4 || marginRatio > 0.15) continue;

    const name = productName(products, productId);

    findings.push({
      id: `dominant-cost-${productId}`,
      severity: marginRatio < 0 ? "alta" : "media",
      title: `En ${name}, el costo de ${dominant.label} se come el margen`,
      message: `El costo de ${dominant.label} representa ${Math.round(share * 100)}% de los costos totales de ${name} (${formatCurrency(dominant.total)} de ${formatCurrency(totalCost)}).`,
      recommendation: `Antes de tocar precios, conviene revisar específicamente el costo de ${dominant.label} en ${name}: buscar otro proveedor, renegociar tarifa, o repensar esa parte del evento.`,
      evidence: componentTotals
        .filter((c) => c.total > 0)
        .sort((a, b) => b.total - a.total)
        .map((c) => ({ concepto: c.label, total: c.total, porcentaje_del_costo: `${Math.round((c.total / totalCost) * 100)}%` })),
    });
  }

  return findings;
}

// -----------------------------------------------------------------------
// Regla 5: productos inactivos / en declive
// -----------------------------------------------------------------------
function ruleInactiveProducts(events, products, from = new Date()) {
  const findings = [];
  const byProduct = groupBy(events, (e) => e.product_id);

  for (const product of products) {
    const productEvents = byProduct.get(product.id) || [];
    if (productEvents.length === 0) continue;

    const lastEvent = productEvents.slice().sort((a, b) => (a.date > b.date ? -1 : 1))[0];
    const idleDays = daysSince(lastEvent.date, from);

    if (idleDays >= 60) {
      findings.push({
        id: `inactive-${product.id}`,
        severity: idleDays >= 120 ? "alta" : "media",
        title: `${product.name} no tiene eventos nuevos hace ${idleDays} días`,
        message: `El último evento de ${product.name} fue el ${formatDate(lastEvent.date)}. Antes tuvo ${productEvents.length} evento(s) en total.`,
        recommendation: `Si no hay eventos planeados para ${product.name}, vale la pena decidir si conviene seguir ofreciéndolo o retirarlo del catálogo.`,
        evidence: productEvents
          .slice()
          .sort((a, b) => (a.date < b.date ? 1 : -1))
          .slice(0, 5)
          .map((e) => ({ date: e.date, description: e.description, profit: eventProfit(e) })),
      });
    }
  }

  return findings;
}

const SEVERITY_ORDER = { alta: 0, media: 1, baja: 2 };

/**
 * @param {Array} events   eventos con campos crudos (income, tarifas, etc.) — sirve tanto un
 *                         resultado de `event_financials` como de `events`, sólo usa los crudos.
 * @param {Array} movements filas de la tabla movements
 * @param {Array} products  filas de la tabla products
 */
export function computeInsights(events, movements, products, from = new Date()) {
  const findings = [
    ...ruleNegativeMarginProducts(events, products),
    ...ruleDeficitMonths(movements),
    ...ruleDecliningTrend(events),
    ...ruleDominantCost(events, products),
    ...ruleInactiveProducts(events, products, from),
  ];

  findings.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  return findings;
}

/** Ganancia por producto y por mes, para el gráfico de tendencia. últimos `count` meses. */
export function profitByProductByMonth(events, products, count = 6, from = new Date()) {
  const months = lastMonths(count, from);
  const byProduct = groupBy(events, (e) => e.product_id);

  return products
    .filter((p) => (byProduct.get(p.id) || []).length > 0)
    .map((p) => ({
      product: p,
      series: months.map((m) => {
        const monthEvents = (byProduct.get(p.id) || []).filter((e) => {
          const d = new Date(e.date + "T00:00:00");
          return d.getFullYear() === m.year && d.getMonth() + 1 === m.month;
        });
        return sum(monthEvents, eventProfit);
      }),
    }));
}

export function quarterComparison(events, from = new Date()) {
  const thisQuarter = eventsInLastMonths(events, 3, from);
  const prevFrom = new Date(from.getFullYear(), from.getMonth() - 3, 1);
  const lastQuarter = eventsInLastMonths(events, 3, prevFrom);

  return {
    current: { events: thisQuarter.length, income: sum(thisQuarter, (e) => e.income), profit: sum(thisQuarter, eventProfit) },
    previous: { events: lastQuarter.length, income: sum(lastQuarter, (e) => e.income), profit: sum(lastQuarter, eventProfit) },
  };
}

function groupBy(arr, keyFn) {
  const map = new Map();
  for (const item of arr) {
    const key = keyFn(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return map;
}
